import { useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import dayjs from "dayjs"
import { PodClass } from "../../../classes/Pod"
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardContent, Checkbox, CircularProgress, Divider, FormControlLabel, IconButton, List, ListItem, ListItemIcon, ListItemText, Switch, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material"
import { useStore } from '../../../contexts/store'
import { PodInformation, Usergroup } from "../../../shared/src/types/Pod"
import { useParams } from "../../../helper/Helper"
import { useTranslation } from "react-i18next"
import { OpCode } from "../../../shared/src/types/OpCodes"
import React from "react"
import { Op_editPermission, Op_editPod } from "../../../shared/src/types/Ops"
import { downloadableType } from "../../../shared/src/types/Miscs"
import Prompt from "../../Elements/Prompt"
import { podKeyphraseMinLength, podKeyphraseMaxLength } from "../../../shared/src/validation/validationConstantsString"
import api from "../../../api/api"
import ConfirmDialog from "../../Elements/ConfirmDialog"
import { Permission } from "../../../shared/src/types/Permissions"
import { CheckBoxOutlined, Clear, Edit, ExpandMore, Folder, FolderOutlined, Lock, LockOpen, PeopleOutline, TextSnippetOutlined, WarningAmber } from "@mui/icons-material"

const Permissions = () => {
  const { podId } = useParams()
  const { podStore, opStore, sessionStore } = useStore()

  const [showPodKeyphrasePrompt, setShowPodKeyphrasePrompt] = useState<boolean>(false)
  const [ removeEditPermissionGroup, setRemoveEditPermissionGroup ] = React.useState<string|null>(null)
  const [ podInfo, setPodInfo ] = useState<null|PodInformation|'failed'|'loading'>(null)

  const { t } = useTranslation()
  const session = sessionStore.session

  const getPodInfo = async(podId:string) => {
    setPodInfo('loading')
    const res = await api.getPodInformation(podId)
    if (res) {
      setPodInfo(res)
    } else {
      setPodInfo('failed')
    }
  }

  useEffect(() => {
    if ((typeof podStore.pod?.hasKeyphrase === 'undefined') && (podInfo !== 'loading')) getPodInfo(podId)
  }, [podId, podInfo, podStore.pod?.hasKeyphrase])

  const permissionExplainer:{[permissionName: string]: string} = {
    'addComment': t('This includes the permission to edit and delete own comments'),
    'addEmotion': t('This includes the permission to edit and delete own emotions'),
    'addLink': t('This includes the permission to edit and delete own links'),
    'addReadingQuestion': t('This includes the permission to edit and delete own reading questions'),
    'addTag': t('This includes the permission to edit and delete own tags'),
    'addTagging': t('This includes the permission to edit and delete own taggings'),
    'addWeblink': t('This includes the permission to edit and delete own weblinks'),
    'addMessage': t('This includes the permission to edit and delete own messages'),

    'sharePencilInteraction': t('Users can always use the pencils for private annotations'),
    'shareHighlighterInteraction': t('Users can always use the highlighter for private annotations'),
    'shareBoxInteraction': t('Users can always use the Freehand Box for private annotations'),

    'editPermission': t('Attention: This permission allows users to expand their own permissions!'),
    'addThread': t('This permission is required if users should respond to new comments.'),
  }

  const pod:PodClass|null = podStore.pod
  if (!pod) return null
  if (!session) return null

  const podGroup:Usergroup = pod.getUsergroupByRole('Pod')
  var adminGroup:Usergroup|null = null

  try {
    adminGroup = pod.getUsergroupByRole('Admin')
  }
  catch(e) {
    // console.warn(e)
  }

  const groupIds = [
    podGroup.usergroupId,
  ] //Object.keys(pod.usergroups)

  if (adminGroup) groupIds.push(adminGroup.usergroupId)

  const editPermission = (usergroupId: string, permission:OpCode, granted: boolean) => {
    //if (!pod.permissions['editPermission']) return null

    const op:Op_editPermission = {
      op: 'editPermission',
      podId: pod.podId,
      data: {
        userId: session.user.userId,
        userName: podStore.userPseudonym || '',
        usergroupId,
        permission,
        granted,
      }
    }
    opStore.doOp(op)
  }

  const permissionRow = (perm:string) => {
    const disabled = ! ((pod.permissions['editPermission']) || (podStore.pod?.creator?.userId === session.user.userId))
    return <TableRow>
      <TableCell sx={{pl:6}}>
        {t(perm)}
        {permissionExplainer[perm] ? <small style={{display:'block'}}>{permissionExplainer[perm]}</small> : null}
      </TableCell>
      {
        groupIds.map(usergroupId => {
          const group = pod.usergroups[usergroupId]
          if ((group.usergroupId !== podGroup.usergroupId) && (podGroup.permissions.indexOf(perm)>-1) && group.permissions.indexOf(perm) == -1) return <TableCell sx={{textAlign:'center'}} title={t('Users in this group have this permission because it is given to all users in the pod')||''} >
            <CheckBoxOutlined onClick={() => editPermission(group.usergroupId, perm as OpCode, true)}
              sx={{color: 'gray', mt:'6px', '&:hover':{ cursor:'pointer' }}}
            />
          </TableCell>
          return <TableCell sx={{textAlign:'center'}}>
            <Checkbox
              disabled={disabled}
              checked={group.permissions.indexOf(perm)>-1}
              onChange={(e) => {
              if ((perm === 'editPermission') && (!e.target.checked) && (podStore.pod?.creator?.userId !== session.user.userId)) {
                setRemoveEditPermissionGroup(group.usergroupId)
              }
              else {
                editPermission(group.usergroupId, perm as OpCode, e.target.checked)
              }
            }}
            />
          </TableCell>
        })
      }
    </TableRow>
  }

  const permissionBlock = (title:string, icon:React.JSX.Element|null, perms: string[]) => {
    const rows  = perms.map(perm => permissionRow(perm))
    rows.unshift(<TableRow>
      <TableCell sx={{pt:8}}>
        {icon}
        <Typography component="span" sx={{fontSize:'1rem', fontWeight:500, ml:1}}>{title}</Typography>
      </TableCell>
        {groupIds.map(group => {
          return <TableCell sx={{textAlign:'center', pt:8, fontWeight:500, width:'10em'}}>{pod.usergroups[group].name}
          <small></small>
          </TableCell>
        })}
      </TableRow>)
    return rows
  }

  const updatePodInfo = () => {
    if (typeof pod.hasKeyphrase === 'undefined') {
      setPodInfo('loading')
      setTimeout(() => {
        getPodInfo(podId)
      }, 6000)
    }
  }

  const deletePodKeyphase = () => {
    const op:Op_editPod = {
      op: 'editPod',
      podId: pod.podId,
      data: {
        mods: {
          keyphrase: null,
          tModified: dayjs().unix()
        },
        userId: session.user.userId,
        usergroupId: pod.getUsergroupByRole('Pod').usergroupId,
        userName: podStore.userPseudonym || '',
      }
    }
    opStore.doOp(op)
    updatePodInfo()
  }

  const changePodSetting = (which:'name'|'description'|'keyphrase', s:string) => {
    if (s) {
      const mods: {
        name?: string,
        description?: string,
        allowDownload?:downloadableType[],
        keyphrase?: string,
        tModified?: number
      } = {
        tModified: dayjs().unix()
      }
      mods[which] = s

      const op:Op_editPod = {
        op: 'editPod',
        podId: pod.podId,
        data: {
          mods,
          userId: session.user.userId,
          usergroupId: pod.getUsergroupByRole('Pod').usergroupId,
          userName: podStore.userPseudonym || '',
        }
      }

      opStore.doOp(op)
      // update if pod has no keyphrase and new one is set
      if(which === 'keyphrase' && podInfo && typeof podInfo === 'object' && podInfo.hasKeyphrase === false) {
        updatePodInfo()
      }
    }
  }

  const setAllowDownload = (which:downloadableType, value:boolean) => {
    const allowDownload = pod.allowDownload.filter(s => s !== which)
    if (value) allowDownload.push(which)
    const op:Op_editPod = {
      op: 'editPod',
      podId: pod.podId,
      data: {
        mods: {
          tModified: dayjs().unix(),
          allowDownload
        },
        userId: session.user.userId,
        usergroupId: pod.getUsergroupByRole('Pod').usergroupId,
        userName: podStore.userPseudonym || '',
      }
    }
    opStore.doOp(op)
  }

  const KeyphraseSettings = observer(({podInfo}: {podInfo: null|PodInformation|'failed'|'loading'}) => {

    if (typeof pod.hasKeyphrase === 'undefined') {
      if(podInfo === null) return null
      if(podInfo === 'failed') return (
        <Alert sx={{m: 1}} severity="error">{t('Loading did not work, check your internet connection and try again')}</Alert>
      )
      if(podInfo === 'loading') return (
        <CircularProgress sx={{marginLeft: "20px"}} size="25px" />
      )
    }

    if ((podInfo && typeof podInfo === 'object' && podInfo.hasKeyphrase) || (pod.hasKeyphrase === true)) {
      return (
        <Box sx={{mt:1, mb:1}}>
          <Lock sx={{verticalAlign:'middle', mr:1}} />
          <span style={{verticalAlign:'middle'}}>{t('The Pod is currently protected with a keyphrase. Users need to know the URL and the keyphrase to join.')}</span>
          <IconButton title={t('Delete Pod Keyphrase')||''} sx={{float:'right', mt:-1}} aria-label="edit" disabled={!pod.permissions['editPod']} onClick={() => {deletePodKeyphase()}}>
            <Clear />
          </IconButton>
          <IconButton title={t('Change Pod Keyphrase')||''} sx={{float:'right', mt:-1}} aria-label="edit" disabled={!pod.permissions['editPod']} onClick={() => {setShowPodKeyphrasePrompt(true)}}>
            <Edit />
          </IconButton>
        </Box>
      )
    }
    if ((podInfo && typeof podInfo === 'object' && !podInfo.hasKeyphrase) || (pod.hasKeyphrase === false)) {
      return (
        <Box sx={{mt:1}}>
          <LockOpen sx={{verticalAlign:'middle', mr:1}} />
          <span style={{verticalAlign:'middle'}}>The Pod currently has no keyphrase set. Anybody can join if they know the URL.</span>
          <IconButton title={t('Change Pod Keyphrase')||''} sx={{float:'right', mt:-1}} aria-label="edit" disabled={!pod.permissions['editPod']} onClick={() => {setShowPodKeyphrasePrompt(true)}}>
            <Edit />
          </IconButton>
        </Box>
      )
    }

    return null
  })

  return <Box>
    <Card elevation={3} sx={{margin:1}}>
      <CardContent>
        <Box>
          <Typography sx={{fontSize:'1rem', fontWeight:'bold'}}>{t('Keyphrase')}:</Typography>
          <KeyphraseSettings podInfo={podInfo} />
        </Box>
      </CardContent>
    </Card>

    <Card elevation={3} sx={{margin:1}}>
      <CardContent>
        <Box>
          <Typography sx={{fontSize:'1rem', fontWeight:'bold'}}>{t('Allow Downloads')}:</Typography>

          <List dense sx={{maxHeight:140, overflow:'auto',  '& ul': { padding: 0 }}}>
            <ListItem sx={{cursor: !pod.permissions['editPermission'] ? 'default' : 'pointer'}} onClick={() => { if (pod.permissions['editPermission']) setAllowDownload('pdf', pod.allowDownload.indexOf('pdf')===-1) }}>
              <ListItemIcon>
                <Checkbox
                  disabled={!pod.permissions['editPermission']}
                  edge="start"
                  checked={pod.allowDownload.indexOf('pdf')>-1}
                  tabIndex={-1}
                  disableRipple
                  sx={{padding:0}}
                />
              </ListItemIcon>
              <ListItemText primary={t('Allow users to download this Pod\'s PDF files')} />
            </ListItem>
            { pod.allowDownload.indexOf('pdf')>-1 && ['annotation','comment','emotion','link','readingQuestion','tagging','weblink'].map(option => {
              const disabled = (!pod.permissions['editPermission']) || (pod.allowDownload.indexOf('pdf')===-1)
              const checked  = (pod.allowDownload.indexOf(option)>-1) && (pod.allowDownload.indexOf('pdf')>-1)
              return <ListItem sx={{cursor: disabled ? 'default' : 'pointer'}} onClick={() => { if (!disabled) setAllowDownload(option, pod.allowDownload.indexOf(option)===-1) }}>
              <ListItemIcon>
                <Checkbox
                  disabled={disabled}
                  edge="start"
                  checked={checked}
                  tabIndex={-1}
                  disableRipple
                  sx={{padding:0}}
                />
              </ListItemIcon>
              <ListItemText primary={t("Allow users to include '{{option}}' interactions", {option: t(option)})} />
            </ListItem>
            })}
          </List>

        </Box>
      </CardContent>
    </Card>


    <Card elevation={3} sx={{margin:1}}>
      <CardContent>
        <Box>
          <Typography sx={{fontSize:'1rem', fontWeight:'bold'}}>{t('Permissions')}:</Typography>
          <Box sx={{mt:1, mb:1}}>{t(`Explain_Pod_Permissions`, {'nofGroups': Object.keys(pod.usergroups).length})}
          </Box>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}><Typography component="span" sx={{fontSize:'1.1rem'}}>{t('Administer Materials and Users for this Pod')}</Typography></AccordionSummary>
            <AccordionDetails>
              <Table>
                <TableBody>
                  { permissionBlock(t('Permissions for Folders'), <FolderOutlined sx={{verticalAlign:'bottom'}} />, ['addFolder', 'editFolder', 'deleteFolder']) }
                  { permissionBlock(t('Permissions for Files'), <TextSnippetOutlined sx={{verticalAlign:'bottom'}}/>, ['addPdfFile', 'editPdfFile', 'deletePdfFile']) }
                  { permissionBlock(t('Administrative Permissions'), <WarningAmber sx={{verticalAlign:'bottom'}}/>, ['editPod', 'editPermission', 'addUserToPod', 'editUserInfo', 'removeUserFromPod']) }
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}><Typography component="span" sx={{fontSize:'1.1rem'}}>{t('Tools and Interaction Types Available for this Pod')}</Typography></AccordionSummary>
            <AccordionDetails>
              <Table>
                <TableBody>
                  { permissionBlock(t('Freehand Tools'),    null, ["sharePencilInteraction", "shareHighlighterInteraction", "shareBoxInteraction"]) }
                  { permissionBlock(t('Annotations'),       null, ['addAnnotation']) }
                  { permissionBlock(t('Comments'),          null, ['addComment', 'editComment', 'deleteComment']) }
                  { permissionBlock(t('Emotions'),          null, ['addEmotion', 'editEmotion', 'deleteEmotion']) }
                  { permissionBlock(t('Links'),             null, ['addLink', 'editLink', 'deleteLink']) }
                  { permissionBlock(t('Reading Questions'), null, ['addReadingQuestion', 'editReadingQuestion', 'deleteReadingQuestion']) }
                  { permissionBlock(t('Taggings'),          null, ['addTagging', 'editTagging', 'deleteTagging']) }
                  { permissionBlock(t('Weblinks'),          null, ['addWeblink', 'editWeblink', 'deleteWeblink']) }
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}><Typography component="span" sx={{fontSize:'1.1rem'}}>{t('Collaboration Inside the Pod')}</Typography></AccordionSummary>
            <AccordionDetails>
              <Table>
                <TableBody>
                  { permissionBlock('Conversations', null, ['addThread', 'addMessage', 'editMessage', 'deleteMessage']) }
                  { permissionBlock('Tags', null, ['addTag', 'editTag', 'deleteTag']) }
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>

        </Box>
      </CardContent>
    </Card>








            <Prompt title={t('Pod Keyphrase')} info={t('Please enter a new keyphrase for this pod') as string} open={showPodKeyphrasePrompt} handleClose={() => { setShowPodKeyphrasePrompt(false) }} onOk={(s:string) => { changePodSetting('keyphrase', s) }} type={{type:'string', minLength: podKeyphraseMinLength, maxLength:podKeyphraseMaxLength}} />
            <ConfirmDialog title={t('Removing \'editPermission\'-Permission')} info={t('You are about to remove the \'editPermission\'-permission. This step might be irreversible. Are you sure you want to continue?')||''} open={Boolean(removeEditPermissionGroup)} handleClose={() => { setRemoveEditPermissionGroup(null)}} onOk={() => {
              editPermission(removeEditPermissionGroup || '', 'editPermission', false)
            }} />
    </Box>
  }

export default observer(Permissions)